import { useState } from 'react';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { ModalShell } from '@/shared/ui/ModalShell';
import { SkeletonGrid } from '@/shared/ui/SkeletonCard';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { semantic, typography, radius } from '@/shared/theme/tokens';
import { useToast, useApiErrorToast } from '@/shared/ui/Toast';
import { useSvgTemplatesAdmin, useDeleteSvgTemplate } from '../hooks/useSvgTemplateAdmin';
import { SvgThumbnail } from './SvgThumbnail';
import { SvgTemplateForm } from './SvgTemplateForm';

/**
 * Global SVG template catalog manager (ripik_admin). Card grid with SVG
 * thumbnails; add / edit / delete. Delete is blocked (409) while a deployment
 * binds the template — the BE message is surfaced.
 */
export function SvgTemplatesManager({ canManage }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const { data: templates = [], isLoading } = useSvgTemplatesAdmin();
  const deleteT = useDeleteSvgTemplate();
  const toast = useToast();
  const toastApiError = useApiErrorToast();

  const [formModal, setFormModal] = useState(null);   // { mode, template? }
  const [deleteTarget, setDeleteTarget] = useState(null);

  async function handleDelete(t) {
    try {
      await deleteT.mutateAsync(t.id);
      setDeleteTarget(null);
      toast({ message: 'Template deleted' });
    } catch (err) { toastApiError(err); }   // surfaces 409 SVG_TEMPLATE_IN_USE
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, fontFamily: typography.fontFamily.body }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary }}>SVG Templates</div>
          <div style={{ fontSize: typography.fontSize.base, color: c.textSubtle }}>
            {templates.length} template{templates.length !== 1 ? 's' : ''} · used to build deployment overview diagrams
          </div>
        </div>
        <span style={{ flex: 1 }} />
        {canManage && (
          <PrimaryButton icon="add" size="sm" onClick={() => setFormModal({ mode: 'create' })}>
            Add Template
          </PrimaryButton>
        )}
      </div>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {isLoading ? (
          <SkeletonGrid count={3} cardHeight={220} />
        ) : templates.length === 0 ? (
          <EmptyState canManage={canManage} onAdd={() => setFormModal({ mode: 'create' })} c={c} />
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 16 }}>
            {templates.map((t) => (
              <div key={t.id} style={{
                borderRadius: radius['2xl'], border: `1px solid ${c.border}`,
                background: isDark ? 'rgba(255,255,255,0.025)' : '#fff', overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
              }}>
                <SvgThumbnail template={t} height={150} />
                <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: c.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                      <div style={{ fontSize: typography.fontSize.sm, color: c.textSubtle }}>{t.sections?.length ?? 0} region{(t.sections?.length ?? 0) !== 1 ? 's' : ''}</div>
                    </div>
                    {canManage && (
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button onClick={() => setFormModal({ mode: 'edit', template: t })} style={iconBtn(c)} title="Edit">
                          <span className="material-symbols-outlined" style={{ fontSize: 15, color: c.textSubtle }}>edit</span>
                        </button>
                        <button onClick={() => setDeleteTarget(t)} style={{ ...iconBtn(c), borderColor: semantic.errorBorder }} title="Delete">
                          <span className="material-symbols-outlined" style={{ fontSize: 15, color: semantic.error }}>delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {formModal && (
        <SvgTemplateForm mode={formModal.mode} template={formModal.template} onClose={() => setFormModal(null)} />
      )}

      {deleteTarget && (
        <ModalShell onClose={() => setDeleteTarget(null)} icon="delete" iconVariant="danger" title="Delete template" maxWidth="420px">
          <p style={{ fontSize: typography.fontSize.lg, color: c.textLabel, margin: '0 0 8px', lineHeight: 1.5 }}>
            Delete <strong>{deleteTarget.name}</strong>?
          </p>
          <p style={{ fontSize: typography.fontSize.base, color: c.textSubtle, margin: '0 0 20px' }}>
            If any deployment still uses this diagram, deletion is blocked until it is unbound.
          </p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <PrimaryButton variant="secondary" onClick={() => setDeleteTarget(null)} disabled={deleteT.isPending}>Cancel</PrimaryButton>
            <PrimaryButton variant="danger" icon="delete" onClick={() => handleDelete(deleteTarget)} disabled={deleteT.isPending}>
              {deleteT.isPending ? 'Deleting…' : 'Delete'}
            </PrimaryButton>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

function EmptyState({ canManage, onAdd, c }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 220, gap: 10, textAlign: 'center' }}>
      <span className="material-symbols-outlined" style={{ fontSize: 40, color: c.textMuted }}>schema</span>
      <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>No SVG templates yet</div>
      <div style={{ fontSize: typography.fontSize.base, color: c.textSubtle, maxWidth: 320 }}>Upload an HMI diagram to make it available for deployment overview pages.</div>
      {canManage && <PrimaryButton icon="add" size="sm" onClick={onAdd}>Add Template</PrimaryButton>}
    </div>
  );
}

function iconBtn(c) {
  return {
    width: 28, height: 28, borderRadius: 7, background: 'transparent', border: `1px solid ${c.border}`,
    display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', padding: 0,
  };
}
